import * as Sentry from "@sentry/react";
import { RegisterFormData } from "@/routes/guestRoutes/register/types/registerSchema";
import { apiEndpoints } from "@/services/api/api";
import Cookies from "js-cookie";
import { RoleEnum } from "@/types/auth/roleEnum";
import { UserInformationData } from "@/types/auth/userInformationData";
import { basketService } from "../basket/basketService";
import { API_CONFIG, AUTH_CONFIG } from "@/utils/constants";

class AuthService {
  private accessToken: string | null;
  private refreshToken: string | null;
  private userRoles: RoleEnum[] | null = null;
  private subscribers: Set<() => void>;
  public userData: UserInformationData | null = null;
  private _initialized = false;
  private _userInfoFetch: Promise<UserInformationData | null> | null = null;

  constructor() {
    this.accessToken = Cookies.get("accessToken") || null;
    this.refreshToken = Cookies.get("refreshToken") || null;
    this.subscribers = new Set();
  }

  // ==========================================
  // TOKEN HANDLING
  // ==========================================
  public getAccessToken(): string | null {
    if (!this.accessToken) {
      this.accessToken = Cookies.get("accessToken") || null;
    }
    return this.accessToken;
  }
  public getRefreshToken(): string | null {
    if (!this.refreshToken) {
      this.refreshToken = Cookies.get("refreshToken") || null;
    }
    return this.refreshToken;
  }

  private setAccessToken(accessToken: string) {
    this.accessToken = accessToken;
  }
  private setRefreshToken(refreshToken: string) {
    this.refreshToken = refreshToken;
  }

  updateAccessToken(accessToken: string) {
    this.accessToken = accessToken;
    Cookies.set("accessToken", accessToken, {
      secure: true,
      sameSite: "strict",
      expires: 1 / 96, // yalan bu 1 dakika
    });
  }
  updateRefreshToken(refreshToken: string) {
    this.refreshToken = refreshToken;
    Cookies.set("refreshToken", refreshToken, {
      secure: true,
      sameSite: "strict",
      expires: 3, // 3 days
    });
  }

  writeTokensToCookie(accessToken: string, refreshToken: string): void {
    Cookies.set("accessToken", accessToken, {
      secure: true,
      sameSite: "strict",
      expires: 1 / 96, // 15 minutes
      path: "/",
    });
    Cookies.set("refreshToken", refreshToken, {
      secure: true,
      sameSite: "strict",
      expires: 3, // 3 days
      path: "/",
    });
  }

  clearTokens() {
    Cookies.remove("accessToken");
    localStorage.removeItem("basketId");
    Cookies.remove("refreshToken");
    this.accessToken = null;
    this.refreshToken = null;
    this.userRoles = null;
    this.userData = null;
    this._initialized = false;
    Sentry.setUser(null);
  }
  // ==========================================
  // AUTHENTICATION METHODS
  // ==========================================
  async loginAndSetUser(username: string, password: string) {
    // USER ROLE DÖNÜP LOGİN ACTION'DAN USER ROLE GORE SAYFA YONLENDIRMESI YAPIYORUZ.
    try {
      const response = await apiEndpoints.loginData(username, password);

      this.setAccessToken(response.data.accessToken);
      this.setRefreshToken(response.data.refreshToken);
      this.writeTokensToCookie(
        response.data.accessToken,
        response.data.refreshToken,
      );

      const [userResponse] = await Promise.all([
        apiEndpoints.getUserData(),
        basketService.getBasket(),
      ]);
      this.userRoles = userResponse.data.roles;
      this.userData = userResponse.data;

      Sentry.setUser({ id: this.userData.id, email: this.userData.email });

      return this.userData;
    } catch (error) {
      this.clearTokens();
      throw error;
    }
  }

  async register(input: RegisterFormData): Promise<boolean> {
    const response = await apiEndpoints.refresh(input);
    if (!response) return false;
    return true;
  }

  async logout() {
    const response = await apiEndpoints.logout();
    this.clearTokens();
    basketService.removeAll();
    return response;
  }

  public async initialize(): Promise<void> {
    if (this._initialized) return;
    const token = this.getAccessToken();
    if (token) {
      try {
        await Promise.all([
          this.getUserInformation(),
          basketService.getBasket(),
        ]);
      } catch {
        this.clearTokens();
      }
    } else if (this.getRefreshToken()) {
      try {
        const rt = this.getRefreshToken()!;
        const response = await fetch(
          `${API_CONFIG.BASE_URL}/${AUTH_CONFIG.ENDPOINTS.AUTH.REFRESH}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ RefreshToken: rt }),
          },
        );
        if (!response.ok) throw new Error("Token refresh failed");
        const result = await response.json();
        if (result.data?.accessToken && result.data?.refreshToken) {
          this.updateAccessToken(result.data.accessToken);
          this.updateRefreshToken(result.data.refreshToken);
          await Promise.all([
            this.getUserInformation(),
            basketService.getBasket(),
          ]);
        } else {
          this.clearTokens();
        }
      } catch {
        this.clearTokens();
      }
    } else {
      basketService.removeAll();
    }
    this._initialized = true;
    this.notifySubscribers();
  }

  public isInitialized(): boolean {
    return this._initialized;
  }

  public async getUserInformation() {
    if (this.userData) return this.userData;
    if (!this.accessToken) return null;
    if (!this._userInfoFetch) {
      this._userInfoFetch = apiEndpoints
        .getUserData()
        .then((response) => {
          this.userData = response.data;
          this.userRoles = this.userData.roles;
          Sentry.setUser({ id: this.userData.id, email: this.userData.email });
          return this.userData;
        })
        .finally(() => {
          this._userInfoFetch = null;
        });
    }
    return this._userInfoFetch;
  }

  public async getUserRole() {
    if (!this.userRoles) {
      const userData = await this.getUserInformation();
      this.userRoles = userData?.roles ?? null;
    }
    return this.userRoles;
  }
  // ==========================================
  // SUBSCRIBE METHODS
  // ==========================================
  subscribe(callback: () => void) {
    this.subscribers.add(callback);

    return () => {
      this.subscribers.delete(callback);
    };
  }

  notifySubscribers() {
    this.subscribers.forEach((subscriber) => {
      subscriber();
    });
  }
}
export const authService = new AuthService();
